import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { app } from '../../firebase-config';
import apiClient from '../../utils/api';
import './QuotesTab.css';

const QuotesTab = ({ quotesData = [], onDataRefresh }) => {
    const { firebaseUser } = useAuth();
    const [localQuotes, setLocalQuotes] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [movingQuoteId, setMovingQuoteId] = useState(null);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    // Form inputs state
    const [type, setType] = useState('text_bg');
    const [selectedSetOption, setSelectedSetOption] = useState('set_1'); 
    const [customSetName, setCustomSetName] = useState(''); 
    const [orderIndex, setOrderIndex] = useState(1); // Tracks initial creation sorting position
    const [text, setText] = useState('');
    const [backgroundImage, setBackgroundImage] = useState('');
    const [quoteImage, setQuoteImage] = useState('');
    const [author, setAuthor] = useState('');

    // Folder View State
    const [selectedFolder, setSelectedFolder] = useState(null);

    const availableSets = ['set_1', 'set_2', 'set_3', 'set_4', 'set_5', 'set_6'];

    useEffect(() => {
        if (quotesData && quotesData.length > 0) {
            setLocalQuotes(quotesData);
        } else {
            const db = getFirestore(app);
            const unsubscribe = onSnapshot(collection(db, "quotes"), (snapshot) => {
                const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setLocalQuotes(list);
            }, (err) => {
                console.error("Client fallback quotes sync error:", err);
            });
            return () => unsubscribe();
        }
    }, [quotesData]);

    const getUniqueSets = () => {
        const sets = new Set();
        localQuotes.forEach(q => {
            const resolvedSetId = (q.setId && q.setId.trim()) ? q.setId.trim() : 'set_1';
            sets.add(resolvedSetId);
        });
        return Array.from(sets).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    };

    const uniqueSets = getUniqueSets();

    const getFolderCounts = () => {
        const counts = {};
        uniqueSets.forEach(set => {
            counts[set] = 0;
        });
        localQuotes.forEach(q => {
            const s = q.setId || 'set_1';
            counts[s] = (counts[s] || 0) + 1;
        });
        return counts;
    };

    const folderCounts = getFolderCounts();

    const handleCreateQuote = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMsg('');

        let targetSetId = selectedSetOption;
        if (selectedSetOption === '__new__') {
            targetSetId = customSetName.trim();
            if (!targetSetId) {
                setError("Please enter a name for your new card Set.");
                return;
            }
        }

        if (type === 'text_bg' && (!text || !backgroundImage)) {
            setError("Both Quote Text and Background Image URL are required.");
            return;
        }
        if (type === 'direct_image' && !quoteImage) {
            setError("Direct Quote Image URL is required.");
            return;
        }

        setSubmitting(true);

        try {
            const idToken = await firebaseUser.getIdToken(true);
            const payload = {
                type,
                setId: targetSetId, 
                orderIndex: Number(orderIndex), // Save sequence positioning
                text: type === 'text_bg' ? text : (text || ''),
                backgroundImage: type === 'text_bg' ? backgroundImage : '',
                quoteImage: type === 'direct_image' ? quoteImage : '',
                author
            };

            await apiClient.post('/quotes', payload, {
                headers: { Authorization: `Bearer ${idToken}` }
            });

            setSuccessMsg(`Quote added to "${targetSetId.toUpperCase()}" successfully!`);
            setText('');
            setBackgroundImage('');
            setQuoteImage('');
            setAuthor('');
            setCustomSetName('');
            setOrderIndex(orderIndex + 1); // Increment automatically to speed up next creations
            setSelectedSetOption(targetSetId);
            
            if (onDataRefresh) {
                onDataRefresh(); 
            }
        } catch (err) {
            console.error("Error creating quote:", err);
            setError(err.response?.data?.message || "Failed to create quote.");
        } finally {
            setSubmitting(false);
        }
    };

    // Generic updater: Handles set changes and/or sorting index modifications securely
    // Moves an existing quote card or updates its sequence order instantly
    const handleUpdateQuoteProperties = async (quote, targetSetId, targetOrderIndex) => {
        setError('');
        setSuccessMsg('');
        setMovingQuoteId(quote.id);

        // Optimistic UI Update: update memory instantly so cards sort without page refreshes
        setLocalQuotes(prev => {
            return prev.map(q => {
                if (q.id === quote.id) {
                    return { ...q, setId: targetSetId, orderIndex: Number(targetOrderIndex) };
                }
                return q;
            });
        });

        try {
            const idToken = await firebaseUser.getIdToken(true);
            const payload = {
                quoteId: quote.id,
                type: quote.type || 'text_bg',
                setId: targetSetId,
                orderIndex: Number(targetOrderIndex), 
                text: quote.text || '',
                backgroundImage: quote.backgroundImage || '',
                quoteImage: quote.quoteImage || '',
                author: quote.author || ''
            };

            await apiClient.post('/quotes', payload, {
                headers: { Authorization: `Bearer ${idToken}` }
            });

            // If the current folder is now empty after moving, return to directories view
            const remainingInFolder = localQuotes.filter(q => {
                const currentSet = q.id === quote.id ? targetSetId : (q.setId || 'set_1');
                return currentSet === selectedFolder;
            });
            if (remainingInFolder.length <= 1 && selectedFolder !== targetSetId) {
                setSelectedFolder(null);
            }
        } catch (err) {
            console.error("Error updating quote properties:", err);
            setError("Failed to update quote sorting metrics.");
            // Revert state from backend database if network fails
            if (onDataRefresh) {
                onDataRefresh();
            }
        } finally {
            setMovingQuoteId(null);
        }
    };

    const handleDeleteQuote = async (quoteId) => {
        if (!window.confirm("Are you sure you want to delete this quote card? This action is permanent.")) {
            return;
        }

        // Optimistic UI Update: remove card from list instantly
        setLocalQuotes(prev => prev.filter(q => q.id !== quoteId));

        try {
            const idToken = await firebaseUser.getIdToken(true);
            await apiClient.delete(`/quotes/${quoteId}`, {
                headers: { Authorization: `Bearer ${idToken}` }
            });
            
            if (filteredQuotes.length <= 1) {
                setSelectedFolder(null);
            }
        } catch (err) {
            console.error("Error deleting quote:", err);
            alert(err.response?.data?.message || "Failed to delete quote.");
            // Revert list from database if network fails
            if (onDataRefresh) {
                onDataRefresh();
            }
        }
    };

    // 1. FILTER by set name, and then 2. SORT cleanly by orderIndex ascending!
    const filteredQuotes = localQuotes
        .filter(q => (q.setId || 'set_1') === selectedFolder)
        .sort((a, b) => (Number(a.orderIndex) || 0) - (Number(b.orderIndex) || 0));

    return (
        <div className="quotes-tab-container vertical-layout">
            
            {/* 1. CREATION PANEL */}
            <div className="quotes-form-panel full-width">
                <h2>Create New Quote Card</h2>
                {error && <p className="quotes-error-alert">{error}</p>}
                {successMsg && <p className="quotes-success-alert">{successMsg}</p>}

                <div className="quote-type-segmented-control">
                    <button 
                        type="button"
                        className={`segmented-tab ${type === 'text_bg' ? 'active' : ''}`}
                        onClick={() => setType('text_bg')}
                    >
                        Text + Background Image
                    </button>
                    <button 
                        type="button"
                        className={`segmented-tab ${type === 'direct_image' ? 'active' : ''}`}
                        onClick={() => setType('direct_image')}
                    >
                        Direct Quote Image
                    </button>
                </div>

                <form onSubmit={handleCreateQuote} className="admin-quote-form vertical-form">
                    <div className="form-row-group">
                        
                        {/* COLUMN 1 */}
                        <div className="form-column">
                            <div className="admin-form-group">
                                <label>Assign to Card Set</label>
                                <select 
                                    value={selectedSetOption} 
                                    onChange={(e) => {
                                        setSelectedSetOption(e.target.value);
                                        if (e.target.value !== '__new__') {
                                            setCustomSetName('');
                                        }
                                    }}
                                    className="styled-select"
                                >
                                    {uniqueSets.map(set => (
                                        <option key={set} value={set}>{set.replace('_', ' ').toUpperCase()}</option>
                                    ))}
                                    <option value="__new__">+ Create New Set...</option>
                                </select>

                                {selectedSetOption === '__new__' && (
                                    <input 
                                        type="text"
                                        value={customSetName}
                                        onChange={(e) => setCustomSetName(e.target.value)}
                                        placeholder="e.g. Independence Day Special"
                                        className="dynamic-set-input"
                                        required
                                    />
                                )}
                            </div>

                            <div className="admin-form-group">
                                <label>Display Position / Order (e.g. 1=First, 2=Second...)</label>
                                <input 
                                    type="number"
                                    value={orderIndex}
                                    onChange={(e) => setOrderIndex(Math.max(1, parseInt(e.target.value) || 1))}
                                    className="styled-select"
                                    min="1"
                                    required
                                />
                            </div>

                            {type === 'text_bg' ? (
                                <div className="admin-form-group">
                                    <label>Quote Text (తెలుగు వాక్యం)</label>
                                    <textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="ఉదాహరణ: నిన్ను నీవు నమ్మడమే విజయానికి మొదటి రహస్యం."
                                        rows="4"
                                        required
                                    />
                                </div>
                            ) : (
                                <div className="admin-form-group">
                                    <label>Social Description (Optional)</label>
                                    <textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder="Enter sharing description text..."
                                        rows="4"
                                    />
                                </div>
                            )}
                        </div>

                        {/* COLUMN 2 */}
                        <div className="form-column">
                            {type === 'text_bg' ? (
                                <div className="admin-form-group">
                                    <label>Background Image URL</label>
                                    <input
                                        type="url"
                                        value={backgroundImage}
                                        onChange={(e) => setBackgroundImage(e.target.value)}
                                        placeholder="https://images.unsplash.com/photo-..."
                                        required
                                    />
                                </div>
                            ) : (
                                <div className="admin-form-group">
                                    <label>Direct Quote Image URL</label>
                                    <input
                                        type="url"
                                        value={quoteImage}
                                        onChange={(e) => setQuoteImage(e.target.value)}
                                        placeholder="https://firebasestorage.googleapis.com/..."
                                        required
                                    />
                                </div>
                            )}

                            <div className="admin-form-group">
                                <label>Author Name (Optional)</label>
                                <input
                                    type="text"
                                    value={author}
                                    onChange={(e) => setAuthor(e.target.value)}
                                    placeholder="e.g. APJ Abdul Kalam"
                                />
                            </div>
                        </div>

                    </div>

                    <button type="submit" className="quotes-submit-btn" disabled={submitting}>
                        {submitting ? "Publishing..." : "Add to Live App"}
                    </button>
                </form>
            </div>

            {/* 2. DYNAMIC LIST */}
            <div className="quotes-list-panel full-width">
                <h2>Active Quote Cards ({localQuotes.length})</h2>

                {selectedFolder === null ? (
                    <div className="folders-grid">
                        {uniqueSets.map((folderKey) => (
                            <div 
                                key={folderKey} 
                                className="folder-card"
                                onClick={() => setSelectedFolder(folderKey)}
                            >
                                <div className="folder-tab" />
                                <div className="folder-body">
                                    <span className="folder-icon">📁</span>
                                    <h3 className="folder-title">{folderKey.replace('_', ' ').toUpperCase()}</h3>
                                    <p className="folder-subtitle">{folderCounts[folderKey] || 0} Cards Inside</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="folder-contents-container">
                        <div className="folder-navigation-bar">
                            <button 
                                className="back-to-folders-btn" 
                                onClick={() => setSelectedFolder(null)}
                            >
                                ← Back to Folders List
                            </button>
                            <span className="active-folder-indicator">
                                Viewing Set Folder: <strong>{selectedFolder.replace('_', ' ').toUpperCase()}</strong> ({filteredQuotes.length} Cards)
                            </span>
                        </div>

                        <div className="quotes-grid">
                            {filteredQuotes.map((quote) => {
                            const isDirect = quote.type === 'direct_image';
                            const isMoving = movingQuoteId === quote.id;

                            return (
                                <div 
                                    key={quote.id} 
                                    className="admin-quote-preview-card"
                                    style={{ 
                                        backgroundImage: isDirect 
                                            ? 'none' 
                                            : `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.85)), url("${quote.backgroundImage}")`,
                                        backgroundSize: isDirect ? 'contain' : 'cover',
                                        backgroundColor: '#0f172a',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        height: '340px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        boxSizing: 'border-box',
                                        padding: '20px',
                                        borderRadius: '12px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                                    }}
                                >
                                    {isDirect ? (
                                        /* Absolute wrapper with solid background guarantees the parent's background never leaks */
                                        <div 
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: '#0f172a',
                                                zIndex: 1
                                            }}
                                        >
                                            <img 
                                                src={quote.quoteImage} 
                                                alt="Direct Preview" 
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <p className="admin-quote-text" style={{ zIndex: 5, color: '#ffffff', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                                            "{quote.text}"
                                        </p>
                                    )}
                                    
                                    {/* Inline styling guarantees the footer sits safely on top of absolute images */}
                                    <div 
                                        className="preview-card-footer" 
                                        style={{ 
                                            position: 'relative', 
                                            zIndex: 5,
                                            background: 'rgba(15, 23, 42, 0.95)',
                                            padding: '12px',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '10px',
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        {quote.author && <span className="admin-quote-author">- {quote.author}</span>}
                                        
                                        {/* Dynamic Sort Position Number (OnBlur triggers auto-save natively) */}
                                        <div className="move-set-container">
                                            <label>Display Position / Order:</label>
                                            <input
                                                type="number"
                                                key={`order-${quote.id}-${quote.orderIndex}`} // Keeps text inputs synchronized with database updates
                                                defaultValue={quote.orderIndex || 0}
                                                disabled={isMoving}
                                                onBlur={(e) => {
                                                    const newVal = Math.max(0, parseInt(e.target.value) || 0);
                                                    if (newVal !== quote.orderIndex) {
                                                        handleUpdateQuoteProperties(quote, quote.setId || 'set_1', newVal);
                                                    }
                                                }}
                                                className="move-set-select"
                                                min="0"
                                                style={{ width: '100%', boxSizing: 'border-box' }}
                                            />
                                        </div>

                                        <div className="move-set-container">
                                            <label>Move Card Set:</label>
                                            <select
                                                value={quote.setId || 'set_1'}
                                                disabled={isMoving}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === '__new_move__') {
                                                        const newSet = window.prompt("Enter the name of the new Set you want to move this card to:");
                                                        if (newSet && newSet.trim()) {
                                                            handleUpdateQuoteProperties(quote, newSet.trim(), quote.orderIndex || 0);
                                                        }
                                                    } else {
                                                        handleUpdateQuoteProperties(quote, val, quote.orderIndex || 0);
                                                    }
                                                }}
                                                className="move-set-select"
                                            >
                                                {uniqueSets.map(set => (
                                                    <option key={set} value={set}>
                                                        {set.replace('_', ' ').toUpperCase()}
                                                    </option>
                                                ))}
                                                <option value="__new_move__">+ Create & Move To...</option>
                                            </select>
                                        </div>

                                        <button 
                                            className="delete-quote-btn" 
                                            onClick={() => handleDeleteQuote(quote.id)}
                                        >
                                            Delete Card
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default QuotesTab;